import { useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, DownloadSimple, ArrowSquareOut } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export function MediaLightboxModal({ open, media, onClose }) {
    useEffect(() => {
        if (!open) return;
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [open, onClose]);

    if (!open || !media) return null;

    const isVideo = media.type === "video" || media.type === "reel";

    const content = (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[9999] flex flex-col items-center justify-center p-3 md:p-6"
                style={{ background: "rgba(5, 10, 17, 0.94)", backdropFilter: "blur(16px)" }}
                onClick={onClose}
                data-testid="chat-media-lightbox-overlay"
            >
                {/* Header controls bar */}
                <div
                    className="w-full max-w-5xl flex items-center justify-between gap-4 py-3 px-4 mb-2 z-10 rounded-2xl"
                    style={{ background: "rgba(255, 255, 255, 0.04)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-[#D4AF37]">
                            {media.type === "reel" ? "Passport Reel" : isVideo ? "Video preview" : "Photo preview"}
                        </div>
                        <div className="text-sm text-white font-medium truncate mt-0.5">
                            {media.title || "Shared media"}
                        </div>
                        {media.subtitle && (
                            <div className="text-xs text-white/50 truncate">
                                {media.subtitle}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                        {media.url && (
                            <a
                                href={media.url}
                                download={media.title || "passport-media"}
                                className="w-9 h-9 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                                title="Download media"
                                onClick={(e) => e.stopPropagation()}
                                data-testid="chat-media-lightbox-download"
                            >
                                <DownloadSimple size={16} />
                            </a>
                        )}

                        {media.path && (
                            <Link
                                to={media.path}
                                onClick={onClose}
                                className="w-9 h-9 rounded-full grid place-items-center bg-white/10 hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                                title="Open full post / reel page"
                                data-testid="chat-media-lightbox-external"
                            >
                                <ArrowSquareOut size={16} />
                            </Link>
                        )}

                        <button
                            type="button"
                            onClick={onClose}
                            className="w-9 h-9 rounded-full grid place-items-center bg-white/10 hover:bg-red-500/20 text-white/80 hover:text-red-300 transition-colors ml-1"
                            title="Close (Esc)"
                            data-testid="chat-media-lightbox-close"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                {/* Media Container */}
                <div
                    className="relative max-w-5xl max-h-[82vh] flex items-center justify-center overflow-hidden rounded-2xl"
                    onClick={(e) => e.stopPropagation()}
                >
                    {isVideo ? (
                        <video
                            src={media.url}
                            controls
                            autoPlay
                            playsInline
                            className="max-w-[92vw] max-h-[80vh] rounded-2xl bg-black object-contain shadow-2xl"
                            style={{ border: "1px solid rgba(212, 175, 55, 0.2)" }}
                            data-testid="chat-media-lightbox-video"
                        />
                    ) : (
                        <img
                            src={media.url}
                            alt={media.title || "Photo"}
                            className="max-w-[92vw] max-h-[80vh] rounded-2xl object-contain shadow-2xl"
                            style={{ border: "1px solid rgba(255, 255, 255, 0.12)" }}
                            data-testid="chat-media-lightbox-image"
                        />
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );

    return typeof document !== "undefined" ? createPortal(content, document.body) : null;
}
